import React from 'react';
import { Box, Typography, Button, LinearProgress } from '@mui/material';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import NavigationIcon from '@mui/icons-material/Navigation';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ListAltIcon from '@mui/icons-material/ListAlt';
import { COLORS } from '../styles';
import { displayGate } from '../utils';

function DriveModeScreen({ driver, currentStopIdx, onArriveAtStop, onEndRun, onViewSummary }) {
  const flights = driver?.flights || [];
  const totalStops = flights.length;
  const completed = currentStopIdx;
  const currentStop = flights[currentStopIdx];
  const progressPct = totalStops > 0 ? (completed / totalStops) * 100 : 0;
  const allDone = currentStopIdx >= totalStops;

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Map placeholder */}
      <Box sx={{
        flex: 1,
        background: `linear-gradient(135deg, #0a1f2e 0%, #1e3a5f 100%)`,
        position: 'relative',
        overflow: 'hidden',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}>
        {/* Pretend route lines */}
        <svg viewBox="0 0 200 280" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', opacity: 0.4 }}>
          <path d="M 30 250 Q 80 180 100 130 Q 130 80 170 30" stroke={COLORS.accent} strokeWidth="3" strokeDasharray="6 4" fill="none" />
        </svg>

        {/* Driver location pin */}
        <Box sx={{
          position: 'absolute',
          left: '50%', top: '50%',
          transform: 'translate(-50%, -50%)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          width: 60, height: 60, borderRadius: '50%',
          backgroundColor: COLORS.accent,
          boxShadow: `0 0 24px ${COLORS.accent}`,
        }}>
          <NavigationIcon sx={{ color: '#0a1020', fontSize: 28 }} />
        </Box>
      </Box>

      {/* Bottom info card */}
      <Box sx={{ backgroundColor: COLORS.surface, p: 2 }}>
        {/* Progress + view summary */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
          <Typography sx={{ fontSize: '0.6rem', color: COLORS.textMuted, textTransform: 'uppercase', letterSpacing: 0.6 }}>
            Drive Mode
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Button
              size="small"
              onClick={onViewSummary}
              startIcon={<ListAltIcon sx={{ fontSize: 12 }} />}
              sx={{
                color: COLORS.accent,
                fontSize: '0.6rem',
                fontWeight: 600,
                textTransform: 'uppercase',
                letterSpacing: 0.4,
                p: 0,
                minWidth: 0,
                '& .MuiButton-startIcon': { mr: 0.5 },
                '&:hover': { backgroundColor: 'transparent', color: COLORS.textPrimary },
              }}
            >
              Summary
            </Button>
            <Typography sx={{ fontSize: '0.7rem', color: COLORS.textPrimary, fontWeight: 600 }}>
              {completed} / {totalStops}
            </Typography>
          </Box>
        </Box>
        <LinearProgress
          variant="determinate"
          value={progressPct}
          sx={{
            height: 4,
            borderRadius: 2,
            backgroundColor: 'rgba(148, 163, 184, 0.2)',
            '& .MuiLinearProgress-bar': { backgroundColor: COLORS.accent },
          }}
        />

        {allDone ? (
          <>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mt: 2 }}>
              <CheckCircleIcon sx={{ color: COLORS.success, fontSize: 28 }} />
              <Box>
                <Typography sx={{ fontSize: '0.85rem', color: COLORS.textPrimary, fontWeight: 700 }}>
                  All stops complete
                </Typography>
                <Typography sx={{ fontSize: '0.65rem', color: COLORS.textMuted }}>
                  Tap below to end the run.
                </Typography>
              </Box>
            </Box>
            <Button
              fullWidth
              onClick={onEndRun}
              sx={{
                mt: 1.5,
                backgroundColor: COLORS.success,
                color: '#0a1020',
                fontWeight: 700,
                py: 1.25,
                fontSize: '0.85rem',
                '&:hover': { backgroundColor: '#16a34a' },
              }}
            >
              END RUN ▸
            </Button>
          </>
        ) : (
          <>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mt: 1.5 }}>
              <LocationOnIcon sx={{ color: COLORS.warn, fontSize: 28 }} />
              <Box sx={{ flex: 1 }}>
                <Typography sx={{ fontSize: '0.6rem', color: COLORS.textMuted, textTransform: 'uppercase', letterSpacing: 0.6 }}>
                  Next Stop
                </Typography>
                <Typography sx={{ fontSize: '0.95rem', color: COLORS.textPrimary, fontWeight: 700, lineHeight: 1 }}>
                  {displayGate(currentStop)} → {currentStop?.destination}
                </Typography>
                <Typography sx={{ fontSize: '0.65rem', color: COLORS.textSecondary, mt: 0.25 }}>
                  {currentStop?.flightNumber}
                  {currentStop?.registration && (
                    <span style={{ color: COLORS.accent, fontFamily: 'monospace', marginLeft: 6 }}>
                      {currentStop.registration}
                    </span>
                  )}
                  {' · '}ETD {currentStop?.etd} · {currentStop?.bags || 0} bags
                </Typography>
              </Box>
            </Box>
            <Button
              fullWidth
              onClick={onArriveAtStop}
              sx={{
                mt: 1.5,
                backgroundColor: COLORS.accent,
                color: '#0a1020',
                fontWeight: 700,
                py: 1.25,
                fontSize: '0.85rem',
                '&:hover': { backgroundColor: COLORS.accentDim },
              }}
            >
              ✓ ARRIVED — DROP BAGS
            </Button>
          </>
        )}
      </Box>
    </Box>
  );
}

export default DriveModeScreen;
